<template>
  <ViewContainer :title="$t('menu.organization_settings')">
    <v-row class="mt-n4">
      <v-col cols="12" sm="12" md="4" lg="3">
        <v-sheet elevation="2" class="rounded pa-2">
          <v-list nav>
            <v-list-item-group
              mandatory
              v-model="option"
              data-testid="organizationSettings-list-menu"
              color="primary"
            >
              <v-list-item
                v-for="option in options"
                :key="option.key"
                :value="option.key"
                @click="optionCliked(option.key)"
              >
                <v-list-item-content :data-tourid="option.tourid">
                  <v-list-item-title class="px-3">{{
                    option.text
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-sheet>
      </v-col>

      <v-col v-if="organization" cols="12" sm="12" md="8" lg="9" class="mb-5">
        <GeneralSettings
          v-show="option === OPTIONS.GENERAL"
          data-testid="organizationSettings-general"
          :organization="organization"
          :isLoading="isLoading"
          :organizationChanged="organizationChanged"
          :logo="logo"
          :mobileLogo="mobileLogo"
          @logoChanged="handleLogoChanged"
          @removeLogo="handleRemoveLogo"
          @update="handleUpdate"
          @reset="resetChanges"
        />
        <SituationTypes
          v-if="option === OPTIONS.SITUATION_TYPES"
          data-testid="organizationSettings-situation-types"
          :organizationId="organizationId"
        />
        <Teams
          v-if="option === OPTIONS.TEAMS"
          data-testid="organizationSettings-teams"
          :organizationId="organizationId"
        />
        <Currency
          v-if="option === OPTIONS.CURRENCY"
          data-testid="organizationSettings-currency"
          :organization="organization"
          :isLoading="isLoading"
          @update="updateOrganization"
        />
        <Categories
          v-if="option === OPTIONS.CATEGORIES"
          data-testid="organizationSettings-categories"
          :organizationId="organizationId"
        />
        <Domains
          v-if="option === OPTIONS.ENABLED_DOMAINS"
          data-testid="organizationSettings-domains"
          :organization="organization"
          :isLoading="isLoading"
          @update="updateOrganization"
        />
        <APIKeys
          v-if="option === OPTIONS.API_KEYS && isAPIKeysEnabled"
          data-testid="organizationSettings-APIkeys"
          :organizationId="organizationId"
        />
        <Webhooks
          v-if="option === OPTIONS.WEBHOOKS"
          data-testid="organizationSettings-webhooks"
          :organizationId="organizationId"
        />
        <AdminSettings
          v-show="option === OPTIONS.ADMIN && isAdmin"
          data-testid="organizationSettings-adminSettings"
          :organization="organization"
          :isLoading="isLoading"
          :organizationChanged="organizationChanged"
          @update="updateAdminSettings"
          @reset="resetChanges"
        />
        <RevMetrics
          v-show="option === OPTIONS.METRICS"
          data-testid="organizationSettings-metrics"
          :organizationId="organizationId"
        />
        <Integrations
          v-if="option === OPTIONS.INTEGRATIONS"
          :organizationId="organizationId"
          data-testid="organizationSettings-integrations"
        />
        <AdminAISettings
          v-if="option === OPTIONS.AI_SETTINGS"
          data-testid="organizationSettings-ai-settings"
          :organization="organization"
        />
      </v-col>
    </v-row>

    <v-dialog scrollable :value="showCustomNamingConfirmation" width="538px">
      <v-card class="pa-0 ma-0"
        ><v-card-title class="px-6 pt-4 pb-3 text-h6">{{
          $t('organization.custom_rev_name_title')
        }}</v-card-title>

        <v-card-text class="px-6 pt-1 pb-0">
          {{ $t('organization.custom_name_confirmation') }}
        </v-card-text>

        <v-card-actions class="px-6 py-4 justify-end">
          <Button
            plain
            color="grey"
            :text="$t('common.button.cancel')"
            @click="showCustomNamingConfirmation = false"
          />
          <Button
            plain
            color="primary"
            :text="$t('common.button.save')"
            @click="updateOrganization"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      data-testid="organizationSettings-dialog-confirmLeave"
      v-model="showDialog"
      max-width="315"
    >
      <v-card>
        <v-card-title class="pb-0">
          <span class="h6 grey--text text--darken-4">{{
            $t('before_leave_dialog.dialog_header')
          }}</span></v-card-title
        >
        <v-card-text class="pb-3">
          <span class="grey--text text--darken-4">
            {{ $t('before_leave_dialog.dialog_subtitle') }}</span
          >
        </v-card-text>
        <v-card-actions class="pb-3 justify-end">
          <Button
            plain
            data-testid="organizationSettingsDialog-button-cancel"
            class="me-6"
            color="grey"
            :text="$t('common.button.cancel')"
            @click="cancelLeaveRoute"
          />
          <Button
            plain
            class="me-5"
            data-testid="organizationSettingsDialog-button-save"
            color="primary"
            :text="$t('common.button.save')"
            @click="handleSave"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </ViewContainer>
</template>

<script>
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'

import GeneralSettings from './General.vue'
import Currency from './Currency.vue'
import SituationTypes from './SituationTypes.vue'
import Teams from './Teams/Teams.vue'
import Categories from './Categories.vue'
import Domains from './Domains.vue'
import Integrations from './Integrations.vue'
import Webhooks from './Webhooks/Container.vue'
import APIKeys from './APIKeys/APIKeysView.vue'
import RevMetrics from './ImpactMetrics.vue'
import Button from '@/components/UI/Button'

import AdminSettings from './AdminSettings.vue'
import AdminAISettings from './AdminAISettings.vue'
import access from '@/lib/access.js'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import _ from 'lodash'
import { mapActions } from 'vuex'

const OPTIONS = {
  GENERAL: 'general',
  ORGANIZATION_STRUCTURE: 'organization_structure',
  CATEGORIES: 'categories',
  SITUATION_TYPES: 'situation_types',
  METRICS: 'metrics',
  TEAMS: 'teams',
  CURRENCY: 'currency',
  ENABLED_DOMAINS: 'domains',
  INTEGRATIONS: 'integrations',
  WEBHOOKS: 'webhooks',
  API_KEYS: 'api_keys',
  ADMIN: 'admin',
  AI_SETTINGS: 'ai_settings',
}

export default {
  name: 'OrganizationSettingsView',

  mixins: [mixin, trackerMixin],

  components: {
    APIKeys,
    ViewContainer,
    GeneralSettings,
    Currency,
    SituationTypes,
    Teams,
    Categories,
    Domains,
    Integrations,
    Webhooks,
    AdminSettings,
    Button,
    RevMetrics,
    AdminAISettings,
  },

  data() {
    return {
      showDialog: false,
      option: this.getRouteQuery()?.tab || 'general',
      isLoading: true,
      organization: {},
      organizationId: this.getRouteParams().organizationId,
      organizationChanged: false,
      router: null,
      OPTIONS,
      originalOrganizationData: {},
      showCustomNamingConfirmation: false,
    }
  },

  mounted() {
    this.getOrganization()
    const routeOption = this.$route?.query?.option
    if (routeOption) {
      this.option = this.OPTIONS[routeOption.toUpperCase()]
    }
  },

  watch: {
    organization: {
      deep: true,
      handler(newValue, oldValue) {
        if (
          oldValue._id &&
          !_.isEqual(newValue, this.originalOrganizationData)
        ) {
          this.organizationChanged = true
        }
      },
    },
  },

  computed: {
    logo() {
      return this.organization.logo
    },

    mobileLogo() {
      return this.organization.mobileLogo
    },

    isAdmin() {
      const account = this.$store.getters.account
      return access.isAdmin(account)
    },

    isSensitiveSettingsEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.orgSensitiveSettings)
    },

    isIntegrationsEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.integrations)
    },

    options() {
      return _.compact([
        this.isSensitiveSettingsEnabled && {
          key: OPTIONS.GENERAL,
          text: this.$t('organization_settings.general_settings'),
          tourid: 'organizationSettings-option-personalization',
        },
        {
          key: OPTIONS.ORGANIZATION_STRUCTURE,
          text: this.$t('organization_settings.organization_structure'),
          tourid: 'organizationSettings-option-organizationStructure',
        },
        {
          key: OPTIONS.CATEGORIES,
          text: this.$t('common.category_other'),
          tourid: 'organizationSettings-option-categories',
        },

        {
          key: OPTIONS.SITUATION_TYPES,
          text: this.$t('settings.situation_types'),
          tourid: 'organizationSettings-option-situationTypes',
        },

        {
          key: OPTIONS.METRICS,
          text: this.$t('organization_settings.impact_metrics'),
          tourid: 'organizationSettings-option-metrics',
        },
        this.isAdmin && {
          key: OPTIONS.TEAMS,
          text: this.$t('common.team_other'),
          tourid: 'organizationSettings-option-teams',
        },

        this.isSensitiveSettingsEnabled && {
          key: OPTIONS.CURRENCY,
          text: this.$t('common.currency'),
          tourid: 'organizationSettings-option-currency',
        },

        this.isSensitiveSettingsEnabled && {
          key: OPTIONS.ENABLED_DOMAINS,
          text: this.$t('common.domain_other'),
          tourid: 'organizationSettings-option-domains',
        },

        this.isSensitiveSettingsEnabled &&
          this.isIntegrationsEnabled && {
            key: OPTIONS.INTEGRATIONS,
            text: this.$t('organization_settings.integrations'),
            tourid: 'organizationSettings-option-integrations',
          },

        this.isSensitiveSettingsEnabled && {
          key: OPTIONS.WEBHOOKS,
          text: this.$t('organization_settings.webhooks'),
          tourid: 'organizationSettings-option-webhooks',
        },

        this.isAPIKeysEnabled &&
          this.isSensitiveSettingsEnabled && {
            key: OPTIONS.API_KEYS,
            text: this.$t('organization_settings.api_keys'),
            tourid: 'organizationSettings-option-apikeys',
          },
        this.isAdmin && {
          key: OPTIONS.ADMIN,
          text: this.$t('organization_settings.admin_settings'),
          tourid: 'organizationSettings-option-adminSettings',
        },
        this.isAdmin && {
          key: OPTIONS.AI_SETTINGS,
          text: this.$t('organization_settings.ai_settings'),
        },
      ])
    },

    isAPIKeysEnabled() {
      return this.organization?.plan !== 'essentials'
    },

    hasCustomNames() {
      const originalNames = this.originalOrganizationData.customNamings
      return !_.isEqual(this.organization.customNamings, originalNames)
    },
  },

  methods: {
    ...mapActions({
      get: 'organizationSettings/getOrganization',
      update: 'organizationSettings/updateOrganization',
      updateSettings: 'updateOrganizationSettings',
    }),

    accessAllowed(acl) {
      return access.aclsAllowedByPlan(this.organization, acl)
    },

    async getOrganization() {
      this.isLoading = true
      try {
        const response = await this.get({
          organizationId: this.organizationId,
        })

        const data = {
          ...response?.data,
          isEnabled: response?.data?.isEnabled ?? true,
          isDeleted: response?.data?.isDeleted ?? false,
          hasIntercomDisabled: response?.data?.hasIntercomDisabled ?? false,
        }

        this.originalOrganizationData = globalThis.structuredClone(data)

        this.organization = data
      } catch (err) {
        this.checkNotFoundError(err)
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async updateOrganization(data = {}) {
      try {
        this.isLoading = true
        await this.update({
          organizationId: this.organizationId,
          organization: {
            ...this.organization,
            logo: this.organization?.logo?.id ?? null,
            mobileLogo: this.organization?.mobileLogo?.id ?? null,
            ...data,
          },
        })

        await this.getOrganization()

        this.showSuccess(this.$t('organization.organization_saved'))
        this.organizationChanged = false
        this.showCustomNamingConfirmation = false
        if (this.router?.next) this.router.next()
      } catch (err) {
        this.showError(this.getErrorMessage(err, data))
      } finally {
        this.isLoading = false
        this.showDialog = false
      }
    },

    async updateAdminSettings(data = {}) {
      try {
        this.isLoading = true
        await this.updateSettings({
          organizationId: this.organizationId,
          data: {
            ...this.organization,
            ...data,
          },
        })

        await this.getOrganization()

        this.showSuccess(this.$t('organization.organization_saved'))
        this.organizationChanged = false
        if (this.router?.next) this.router.next()
      } catch (err) {
        this.showError(this.getErrorMessage(err, data))
      } finally {
        this.isLoading = false
        this.showDialog = false
      }
    },

    cancelLeaveRoute() {
      this.showDialog = false
      this.router = null
    },

    resetChanges() {
      const originalOrganizationData = _.cloneDeep(
        this.originalOrganizationData
      )
      this.organization = originalOrganizationData

      this.organizationChanged = false
    },

    handleLogoChanged(type, mediaObject) {
      this.organization = { ...this.organization, [type]: mediaObject }
    },

    handleRemoveLogo(type) {
      this.organization = { ...this.organization, [type]: null }
    },

    handleSave() {
      return this.option === OPTIONS.ADMIN
        ? this.updateAdminSettings()
        : this.updateOrganization()
    },

    getOrganizationError(errorData, data) {
      const message = errorData.tmessage.toLowerCase()
      return message === 'one_of_the_domains_already_exists'
        ? this.$t(`error.organization.${message}`, {
            domain: data?.domains.slice(-1).pop(),
          })
        : this.$t('error.organization.organization_name_already_exists')
    },

    getErrorMessage(err, data) {
      const errorStatus = err.response.status
      const errorData = err.response.data

      if (errorStatus === 406) return this.getOrganizationError(errorData, data)

      this.handleRequestError(err)
    },

    optionCliked(option) {
      if (option === OPTIONS.ORGANIZATION_STRUCTURE) {
        return this.navigateTo({
          name: 'organizationStructure',
          params: { organizationId: this.organizationId },
        })
      }
    },

    handleUpdate() {
      if (this.hasCustomNames) {
        this.showCustomNamingConfirmation = true
        return
      }

      this.updateOrganization()
    },
  },

  beforeDestroy() {
    this.organization = {}
  },

  beforeRouteLeave(to, from, next) {
    if (this.organizationChanged) {
      this.showDialog = true
      this.router = { to, from, next }
      return next(false)
    } else {
      return next()
    }
  },
}
</script>
